import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { Reveal, SectionHeading } from "../components/primitives";
import { PageHero, CTASection, Icon } from "../components/sections";
import { PROBLEMS, MORE_PROBLEMS } from "../data/site";
import { tr } from "../lib/language";

const ProblemBlock = ({ p, i }) => (
  <Reveal delay={(i % 2) * 0.08} className="h-full">
    <article data-testid={`driving-problem-${i}`} className="flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-dwc-soft text-dwc-green">
        <Icon name={p.icon} />
      </span>
      <h2 className="mt-5 text-lg font-extrabold tracking-tight text-dwc-charcoal">{tr(p.title)}</h2>
      <p className="mt-2 text-sm leading-relaxed text-dwc-charcoal/70">{tr(p.text)}</p>
      {p.psych && (
        <p className="mt-4 rounded-xl bg-dwc-soft p-4 text-[13px] leading-relaxed text-dwc-charcoal/70">
          <span className="font-extrabold text-dwc-green">{tr("Why it happens:")} </span>
          {tr(p.psych)}
        </p>
      )}
      <div className="mt-auto pt-5">
        <Link to={`/blog/${p.guide}`} data-testid={`problem-guide-link-${i}`} className="group inline-flex items-center gap-1.5 text-[13px] font-extrabold uppercase tracking-wide text-dwc-crimson transition-colors hover:text-dwc-green">
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          {tr("Read the Guide")}
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
        </Link>
      </div>
    </article>
  </Reveal>
);

const DrivingProblems = () => (
  <>
    <PageHero
      eyebrow="Driving Problems & Confidence Challenges"
      lines={["Whatever Is Stopping You,", "It Has a Name — and a Method."]}
      text="Every nervous driver faces something specific: traffic, parking, highways, night roads, an old bad experience. Find your problem below, understand why it happens, and read the guide that explains the way out."
    />
    <section className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="The Common Six"
          number="01"
          title="The Problems We Hear Every Day"
          text="These six challenges bring most learners to DWC. Each links to a practical, psychology-aware guide."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {PROBLEMS.map((p, i) => (
            <ProblemBlock key={tr(p.title)} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
    <section className="bg-dwc-soft py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="More Driving Problems"
          number="02"
          title="The Quieter Struggles"
          text="Less talked about, just as real. Night roads, monsoon rain, old scares, tight basements and the moment your mind goes blank under pressure."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {MORE_PROBLEMS.map((p, i) => (
            <ProblemBlock key={tr(p.title)} p={p} i={i + 6} />
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-16 rounded-3xl bg-dwc-green p-10 text-white grain relative overflow-hidden md:p-14">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-200">How DWC Solves These</p>
            <h2 className="mt-3 max-w-3xl text-2xl font-extrabold tracking-tight md:text-3xl">Every problem above is understood through the same method: your Learning DNA, Reaction DNA and Confidence DNA.</h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/80">A practical DPR assessment in your own car shows exactly where the difficulty lives — fear, reaction, awareness, decisions or confidence — and your training is personalised from there.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/driving-psychology" data-testid="problems-psychology-link" className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-dwc-green">
                Driving Psychology <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link to="/programs" data-testid="problems-programs-link" className="inline-flex items-center gap-2 rounded-xl border-2 border-white/60 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white">
                The Three DNA Levels <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
    <CTASection title="Not Sure Which Problem Is Yours?" sub="Book an appointment — the DPR assessment will show exactly where to start." chapter="03" />
  </>
);

export default DrivingProblems;
